import React from 'react';
import { Box, Container, Typography, Button, Paper, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const NotFound: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();

    const handleGoHome = () => {
        navigate('/myshops');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'dark' ? '' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.1)',
                    zIndex: 1
                }
            }}
        >
            {/* Floating animated background elements */}
            <Container maxWidth="md" sx={{ zIndex: 2, position: 'relative' }}>
                <Paper
                    elevation={24}
                    sx={{
                        p: { xs: 3, md: 6 },
                        textAlign: 'center',
                        background:
                            theme.palette.mode === 'dark' ? 'rgba(50, 50, 50, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        animation: `${fadeIn} 0.8s ease-out`
                    }}
                >
                    {/* Main Message */}
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 2,
                            fontSize: { xs: '2.5rem', md: '3.5rem' }
                        }}
                    >
                        404
                    </Typography>

                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            mb: 3,
                            fontSize: { xs: '1.5rem', md: '2rem' }
                        }}
                    >
                        {t('errors.pageNotFound')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}
                    >
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<ArrowBack />}
                            onClick={handleGoHome}
                            sx={{
                                px: 4,
                                py: 1.5,
                                mt: 5,
                                textTransform: 'none',
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                                }
                            }}
                        >
                            {'Go to Dashboard'}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default NotFound;
